"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { X, Send, Sparkles, Minimize2, Upload, FileText } from "lucide-react"
import { cn } from "@/lib/utils"
import { supabase } from "@/lib/supabase"
import { apiRequest } from "@/lib/api-request"
import { getSelectedCompanyId } from "@/lib/active-company"
import RatingButtons from "@/components/ai-chat/RatingButtons"
import { chatAnalytics } from "@/lib/chat-analytics"
import { getApiUrl } from "@/lib/api-config"
import { withIdempotentRetry } from "@/lib/idempotent-fetch"
import { classifyBobChatHttpError, classifyBobChatNetworkError } from "@/lib/bob-chat-error"
import { uploadEntityAttachments } from "@/lib/entity-attachments"
import { colors } from "@/design-system/colors"

interface Message {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: Date
  attachment_mode?: string
  proposal_id?: string
  daily_log_id?: string
  payment_id?: string
  bill_id?: string
}

// AF-35 (2026-09-07): carries the HTTP status + parsed body across the
// throw/catch boundary so the catch block can classify it (see
// @/lib/bob-chat-error) instead of showing one generic message for every
// failure kind.
class BobChatHttpError extends Error {
  status: number
  body: unknown
  constructor(status: number, body: unknown) {
    super(`Bob chat request failed with status ${status}`)
    this.name = 'BobChatHttpError'
    this.status = status
    this.body = body
  }
}

interface AIChatWidgetProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * The company the caller works in right now, as the backend resolves it (GET /api/me/companies: the person's live memberships; the chosen company, else
 * the default one). Never from user_metadata, which can be stale and would cause legitimate storage-path uploads to be rejected by the backend's ownership
 * check (BOB-FINAL-18).
 */
async function getCallerCompanyId(): Promise<string | null> {
  try {
    const list = await apiRequest<Array<{ company_id: string; is_default: boolean }>>('api/me/companies')
    const chosen = getSelectedCompanyId()
    const pick = chosen ? list.find((c) => c.company_id === chosen) : list.find((c) => c.is_default)
    return pick?.company_id || null
  } catch {
    return null
  }
}

async function getReliableUserAndCompany(): Promise<{ userId: string | null; companyId: string | null }> {
  const { data: { session } } = await supabase.auth.getSession()
  const userId = session?.user?.id || null
  if (!userId) return { userId: null, companyId: null }
  return { userId, companyId: await getCallerCompanyId() }
}

export function AIChatWidget({ isOpen, onClose }: AIChatWidgetProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      role: "assistant",
      content: "Hello! I'm Bob, your construction AI assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [attachmentMode, setAttachmentMode] = useState<string | null>(null)
  const [proposalId, setProposalId] = useState<string | null>(null)
  const [dailyLogId, setDailyLogId] = useState<string | null>(null)
  const [paymentId, setPaymentId] = useState<string | null>(null)
  const [billId, setBillId] = useState<string | null>(null)
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([])
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  // AF-33 (2026-09-07): the in-flight /api/bob-chat request's controller,
  // so it can be cancelled explicitly (Cancel button) or on unmount --
  // the backend's own AbortController (server.js's /api/bob-chat route,
  // AF-07) already reacts correctly to the client closing the connection,
  // settling credits and stopping in-flight LLM calls -- this just needed
  // to actually be wired up from the frontend.
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cancel any in-flight request when this component unmounts, so it
  // never tries to setState after unmount and never keeps burning
  // credits/tokens for a response nobody can see anymore.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user?.id) {
        setCurrentUserId(session.user.id)
      }
    }
    getCurrentUser()
  }, [])

  // Track chat widget open/close
  useEffect(() => {
    if (isOpen) {
      // Get user info for analytics
      const getCurrentUser = async () => {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user?.id) {
          const userId = session.user.id
          chatAnalytics.trackChatOpen(userId, (await getCallerCompanyId()) || undefined)
        }
      }
      getCurrentUser()
    } else {
      // Track chat close
      chatAnalytics.trackChatClose(currentUserId || undefined)
    }
  }, [isOpen, currentUserId])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setIsLoading(true)

    // AF-33 (2026-09-07): a fresh controller per send -- isLoading already
    // prevents two overlapping sends from this widget, but this also
    // covers the defensive case and gives the Cancel button and unmount
    // cleanup something real to abort.
    const controller = new AbortController()
    abortControllerRef.current = controller

    // Track message sent
    chatAnalytics.trackMessageSent(input, currentUserId || undefined)
    
    // Reset attachment mode when user sends a message
    setAttachmentMode(null)
    setProposalId(null)
    setDailyLogId(null)
    setPaymentId(null)
    setBillId(null)
    setUploadingFiles([])

    try {
      // Send the entire conversation history for context
      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content
      }))

      // NOTE: identity (user + company) is derived server-side from the
      // Authorization header (attached globally by authenticated-fetch.ts)
      // and JWT + user_company_relation. The backend ignores any identity
      // fields sent in the body, so none are sent here (BOB-FINAL-18).
      // Idempotency-Key: one per user-initiated send, reused automatically
      // if this specific attempt needs a single network-level retry (see
      // withIdempotentRetry) -- protects against a double-submit or a
      // dropped connection duplicating this message's mutation, without
      // ever colliding with a later, genuinely different message.
      const response = await withIdempotentRetry(getApiUrl('api/bob-chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: conversationHistory,
          session_id: chatAnalytics.getSessionId() // Send frontend session ID
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        // AF-35 (2026-09-07): classify by status BEFORE throwing, so the
        // catch block below can show a specific message (insufficient
        // credits / rate limited / unauthorized / validation / server
        // error) instead of one generic string for every failure kind.
        let body: unknown = null
        try { body = await response.json() } catch { /* non-JSON error body -- body stays null, still classified by status alone */ }
        throw new BobChatHttpError(response.status, body)
      }

      const data = await response.json()
      
      const assistantMessage: Message = {
        id: data.message_id || (Date.now() + 1).toString(), // Use backend message ID if available
        role: "assistant",
        content: data.message,
        timestamp: new Date(),
        attachment_mode: data.attachment_mode,
        proposal_id: data.proposal_id,
        daily_log_id: data.daily_log_id,
        payment_id: data.payment_id,
        bill_id: data.bill_id,
      }
      
      setMessages((prev) => [...prev, assistantMessage])
      
      // Track response received
      chatAnalytics.trackResponseReceived(
        data.message.length, 
        data.usage?.conversational?.total_time || 0,
        currentUserId || undefined
      )
      
      // Set attachment mode if Bob is asking for attachments
      if (data.attachment_mode === 'proposal' && data.proposal_id) {
        setAttachmentMode(data.attachment_mode)
        setProposalId(data.proposal_id)
      } else if (data.attachment_mode === 'daily_log' && data.daily_log_id) {
        setAttachmentMode(data.attachment_mode)
        setDailyLogId(data.daily_log_id)
      } else if (data.attachment_mode === 'invoice_payment' && data.payment_id) {
        setAttachmentMode(data.attachment_mode)
        setPaymentId(data.payment_id)
      } else if (data.attachment_mode === 'bill' && data.bill_id) {
        setAttachmentMode(data.attachment_mode)
        setBillId(data.bill_id)
      }
      
      // Handle tool invocations (e.g., create todos, create leads)
      if (data.tool_invocation) {
        console.log('[Bob Chat] Tool invoked:', data.tool_invocation.tool);
        console.log('[Bob Chat] Structured data:', data.tool_invocation.data);
        
        // Track tool usage
        chatAnalytics.trackToolUsed(
          data.tool_invocation.tool,
          data.tool_invocation.result ? true : false,
          currentUserId || undefined
        )
        
        // Handle create_todos tool
        if (data.tool_invocation.tool === 'create_todos') {
          const result = data.tool_invocation.result;
          
          if (result) {
            console.log(`[Bob Chat] ✅ Created ${result.created} todos successfully`);
            
            if (result.errors && result.errors.length > 0) {
              console.warn('[Bob Chat] ⚠️ Some todos failed to create:', result.errors);
            }
            
            // Show success notification
            if (result.created > 0) {
              console.log('[Bob Chat] Todos created:', result.todos);
              // Future: Show toast notification or update UI
              // e.g., toast.success(`Created ${result.created} todo(s)!`)
            }
          }
        }
        
        // Handle create_leads tool
        if (data.tool_invocation.tool === 'create_leads') {
          const result = data.tool_invocation.result;
          
          if (result) {
            console.log(`[Bob Chat] ✅ Created ${result.created} leads successfully`);
            
            if (result.errors && result.errors.length > 0) {
              console.warn('[Bob Chat] ⚠️ Some leads failed to create:', result.errors);
            }
            
            // Show success notification
            if (result.created > 0) {
              console.log('[Bob Chat] Leads created:', result.leads);
              // Future: Show toast notification or update UI
              // e.g., toast.success(`Created ${result.created} lead(s)!`)
            }
          }
        }
      }
    } catch (error) {
      // AF-33 (2026-09-07): a deliberate cancellation (Cancel button or
      // unmount) is NOT a connectivity error -- surfacing an error message
      // for something the user (or navigation) intentionally stopped would
      // be misleading. Just clean up silently.
      //
      // AF-35 (2026-09-07): every OTHER failure used to show the exact
      // same "having trouble connecting" text regardless of cause -- a
      // real 5xx, a genuine network drop, insufficient credits (402), rate
      // limiting (429), and a session/auth problem (401/403) are all
      // different situations with different correct next actions (retry
      // now vs wait vs check billing vs re-login); collapsing them left
      // the user unable to tell which one they were in.
      const info = error instanceof BobChatHttpError
        ? classifyBobChatHttpError(error.status, error.body)
        : classifyBobChatNetworkError(error)

      if (info.kind !== 'cancelled') {
        console.error('Error sending message to Bob:', info.kind, error)

        // Show error message to user
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: info.message,
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
      }
    } finally {
      // Only clear loading/the ref if THIS send is still the active one --
      // avoids a stale finally (from a cancelled/superseded request)
      // clobbering a newer send's state.
      if (abortControllerRef.current === controller) {
        setIsLoading(false)
        abortControllerRef.current = null
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (attachmentMode === 'proposal' || attachmentMode === 'daily_log' || attachmentMode === 'invoice_payment' || attachmentMode === 'bill') {
      setIsDragOver(true)
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (attachmentMode === 'proposal' || attachmentMode === 'daily_log' || attachmentMode === 'invoice_payment' || attachmentMode === 'bill') {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set to false if we're leaving the main container
    if (e.currentTarget === e.target) {
      setIsDragOver(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    if (attachmentMode === 'proposal' && proposalId) {
      const files = Array.from(e.dataTransfer.files).filter(file => 
        file.type === 'application/pdf'
      )

      if (files.length === 0) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Please only upload PDF files.`,
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
        return
      }

      await uploadProposalFiles(files)
    } else if (attachmentMode === 'daily_log' && dailyLogId) {
      const files = Array.from(e.dataTransfer.files)

      if (files.length === 0) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Please select files to upload.`,
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
        return
      }

      await uploadDailyLogFiles(files)
    } else if (attachmentMode === 'invoice_payment' && paymentId) {
      const files = Array.from(e.dataTransfer.files)

      if (files.length === 0) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Please select files to upload.`,
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
        return
      }

      await uploadPaymentFiles(files)
    } else if (attachmentMode === 'bill' && billId) {
      const files = Array.from(e.dataTransfer.files)

      if (files.length === 0) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Please select files to upload.`,
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
        return
      }

      await uploadBillFiles(files)
    }
  }

  const uploadProposalFiles = async (files: File[]) => {
    if (!proposalId) return

    setUploadingFiles(files)
    
    try {
      const { userId, companyId } = await getReliableUserAndCompany()

      if (!userId || !companyId) {
        throw new Error('User not authenticated')
      }

      // Use the same upload function as the proposals store
      const { uploadProposalAttachments } = await import('@/features/leads/proposals/store')
      
      const uploadedAttachments = await uploadProposalAttachments(
        proposalId,
        files,
        companyId,
        userId
      )

      if (uploadedAttachments.length > 0) {
        // Add success message
        const successMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `✅ Successfully uploaded ${uploadedAttachments.length} PDF file(s) to the proposal!`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, successMessage])
      } else {
        // Add error message
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Failed to upload files. Please try again.`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, errorMessage])
      }
      
      // Reset attachment mode
      setAttachmentMode(null)
      setProposalId(null)
      setUploadingFiles([])

    } catch (error) {
      console.error('Upload error:', error)
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `❌ Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      }
      
      setMessages((prev) => [...prev, errorMessage])
      setAttachmentMode(null)
      setProposalId(null)
      setUploadingFiles([])
    }
  }

  const uploadDailyLogFiles = async (files: File[]) => {
    if (!dailyLogId) return

    setUploadingFiles(files)
    
    try {
      const { userId, companyId } = await getReliableUserAndCompany()

      if (!userId || !companyId) {
        throw new Error('User not authenticated')
      }

      // Use the same upload function as the daily logs store
      const { uploadDailyLogAttachments } = await import('@/features/project-management/daily-logs/store')
      
      const uploadedAttachments = await uploadDailyLogAttachments(dailyLogId, files, companyId, userId)

      if (uploadedAttachments.length > 0) {
        // Add success message
        const successMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `✅ Successfully uploaded ${uploadedAttachments.length} file(s) to the daily log! You can drag and drop more files or click "Done" to finish.`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, successMessage])
        
        // Keep attachment mode open for more uploads
        // Don't reset attachmentMode here - let user decide when to close
      } else {
        // Add error message
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Failed to upload files. Please try again.`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, errorMessage])
      }
      
      // Only reset uploading files, keep attachment mode open
      setUploadingFiles([])

    } catch (error) {
      console.error('Upload error:', error)
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `❌ Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}. You can try again or click "Done" to finish.`,
        timestamp: new Date(),
      }
      
      setMessages((prev) => [...prev, errorMessage])
      // Keep attachment mode open so user can try again
      setUploadingFiles([])
    }
  }

  const uploadPaymentFiles = async (files: File[]) => {
    if (!paymentId) return

    setUploadingFiles(files)
    
    try {
      const { userId, companyId } = await getReliableUserAndCompany()

      if (!userId || !companyId) {
        throw new Error('User not authenticated')
      }

      // Shared attachments implementation (entity type invoice_payment):
      // Uploaded through the API: the server stores the file and registers it (and drops the file if the record is refused).
      const uploadedAttachments = await uploadEntityAttachments('invoice_payment', paymentId, files, companyId, userId)

      if (uploadedAttachments.length > 0) {
        // Add success message
        const successMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `✅ Successfully uploaded ${uploadedAttachments.length} file(s) as proof of payment! You can drag and drop more files or click "Done" to finish.`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, successMessage])
        
        // Keep attachment mode open for more uploads
        // Don't reset attachmentMode here - let user decide when to close
      } else {
        // Add error message
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Failed to upload files. Please try again.`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, errorMessage])
      }
      
      // Clear uploading files after success or failure
      setUploadingFiles([])
      
    } catch (error) {
      console.error('Error uploading payment files:', error)
      
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `❌ Error uploading files: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      }
      
      setMessages((prev) => [...prev, errorMessage])
      // Keep attachment mode open so user can try again
      setUploadingFiles([])
    }
  }

  const uploadBillFiles = async (files: File[]) => {
    if (!billId) return

    setUploadingFiles(files)
    
    try {
      const { userId, companyId } = await getReliableUserAndCompany()

      if (!userId || !companyId) {
        throw new Error('User not authenticated')
      }

      // Use the same upload function as the bills store
      const { uploadBillAttachments } = await import('@/features/cost-management/bills/store')
      
      const uploadedAttachments = await uploadBillAttachments(
        billId,
        files,
        companyId,
        userId
      )

      if (uploadedAttachments.length > 0) {
        // Add success message
        const successMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `✅ Successfully uploaded ${uploadedAttachments.length} file(s) to the bill!`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, successMessage])
      } else {
        // Add error message
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: `❌ Failed to upload files. Please try again.`,
          timestamp: new Date(),
        }
        
        setMessages((prev) => [...prev, errorMessage])
      }
      
      // Reset attachment mode
      setAttachmentMode(null)
      setBillId(null)
      setUploadingFiles([])

    } catch (error) {
      console.error('Upload error:', error)
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `❌ Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      }
      
      setMessages((prev) => [...prev, errorMessage])
      setAttachmentMode(null)
      setBillId(null)
      setUploadingFiles([])
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    })
  }

  return (
    <>
      {/* Chat Panel */}
      <div
        className={cn(
          // BUG-5 fix (2026-09-19): the fixed 420px width overflowed any
          // viewport narrower than that (confirmed live down to 320px).
          // `w-full` below `md` makes the panel exactly viewport-width
          // (right-0 keeps its right edge pinned, so it never exceeds the
          // viewport either way) -- simplest fix, no new breakpoint of its
          // own, reuses the same `md` token every other responsive switch
          // in the app already keys off. Desktop (`md:` and up) keeps the
          // original fixed-width floating panel unchanged.
          "fixed bottom-8 right-0 h-[600px] w-full md:w-[420px] bg-white dark:bg-gray-900 border-l border-t border-b border-gray-400",
          "shadow-2xl transition-all duration-300 ease-in-out z-50",
          "flex flex-col rounded-tl-xl rounded-bl-xl",
          isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0",
          isDragOver && (attachmentMode === 'proposal' || attachmentMode === 'daily_log' || attachmentMode === 'invoice_payment' || attachmentMode === 'bill') ? "border-blue-500 bg-blue-50" : ""
        )}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-400 bg-gray-100 dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground">Bob</h3>
              <p className="text-xs text-muted-foreground">Always here to help</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((message) => (
            <div key={message.id} className={cn("flex flex-col", message.role === "user" ? "items-end" : "items-start")}>
              <div
                className={cn(
                  "max-w-[85%] rounded-lg px-4 py-2.5 text-sm",
                  message.role === "user" 
                    ? "bg-blue-600 text-white" 
                    : "bg-gray-200 text-gray-900 border border-gray-400",
                )}
              >
                <p className="leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
                <p className={cn(
                  "text-xs mt-1",
                  message.role === "user" ? "text-blue-100" : "text-gray-500"
                )}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
              
              {/* Rating buttons for assistant messages */}
              {message.role === "assistant" && currentUserId && (
                <div className="mt-2 max-w-[85%] flex justify-end">
                  <RatingButtons
                    messageId={message.id}
                    userId={currentUserId}
                    onRatingSubmitted={(rating, ratingId) => {
                      console.log('Rating submitted:', rating, ratingId)
                      // Track rating submission (convert string to number)
                      const numericRating = rating === 'good' ? 5 : 1
                      chatAnalytics.trackRatingSubmitted(numericRating, message.id, currentUserId || undefined)
                    }}
                  />
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start items-center gap-2">
              <div className="bg-muted rounded-lg px-4 py-2.5">
                <div className="flex gap-1">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.3s]" />
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.15s]" />
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Drag and Drop Indicator */}
        {isDragOver && (attachmentMode === 'proposal' || attachmentMode === 'daily_log' || attachmentMode === 'invoice_payment' || attachmentMode === 'bill') && (
          <div className="absolute inset-0 bg-blue-100/90 border-2 border-dashed border-blue-500 rounded-lg flex items-center justify-center z-20 pointer-events-none">
            <div className="text-center">
              <Upload className="h-12 w-12 text-blue-500 mx-auto mb-2" />
              <p className="text-blue-700 font-medium">
                {attachmentMode === 'proposal' ? 'Drop PDF files here to attach to proposal' : 
                 attachmentMode === 'daily_log' ? 'Drop files here to attach to daily log' :
                 attachmentMode === 'bill' ? 'Drop files here to attach to bill' :
                 'Drop files here to attach as proof of payment'}
              </p>
            </div>
          </div>
        )}

        {/* Upload Status */}
        {uploadingFiles.length > 0 && (
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <Upload className="h-4 w-4 animate-spin" />
              <span>Uploading {uploadingFiles.length} file(s)...</span>
            </div>
          </div>
        )}

        {/* Attachment Mode Indicator */}
        {(attachmentMode === 'proposal' || attachmentMode === 'daily_log' || attachmentMode === 'invoice_payment' || attachmentMode === 'bill') && (
          <div className="px-4 py-2 bg-green-50 border-t border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <FileText className="h-4 w-4" />
                <span>
                  {attachmentMode === 'proposal' 
                    ? 'Drag and drop PDF files to attach to proposal' 
                    : attachmentMode === 'daily_log'
                    ? 'Drag and drop files to attach to daily log'
                    : attachmentMode === 'bill'
                    ? 'Drag and drop files to attach to bill'
                    : 'Drag and drop files to attach as proof of payment'
                  }
                </span>
              </div>
              {(attachmentMode === 'daily_log' || attachmentMode === 'invoice_payment' || attachmentMode === 'bill') && (
                <button
                  onClick={() => {
                    setAttachmentMode(null)
                    setUploadingFiles([])
                    if (attachmentMode === 'daily_log') {
                      setDailyLogId(null)
                    } else if (attachmentMode === 'invoice_payment') {
                      setPaymentId(null)
                    } else if (attachmentMode === 'bill') {
                      setBillId(null)
                    }
                  }}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-400 bg-gray-50 dark:bg-gray-800">
          <div className="flex gap-2">
            <Input
              id="bob-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 bg-background"
              disabled={isLoading}
            />
            <Button onClick={handleSend} disabled={!input.trim() || isLoading} size="icon" className="shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-background/60 backdrop-blur-[2px] z-[var(--z-chat-widget)] transition-opacity duration-300"
          onClick={onClose}
        />
      )}
    </>
  )
}

export function AIChatFloatingButton({ onClick, onDismiss }: { onClick: () => void; onDismiss?: () => void }) {
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Button
        id="open-bob-ai-chat-btn"
        onClick={onClick}
        className={cn(
          "h-14 rounded-full shadow-lg transition-all duration-300",
          "text-white",
          "flex items-center gap-2 px-5",
        )}
        style={{ backgroundColor: colors.buttonSecondary }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonSecondary;
        }}
      >
        <Sparkles className="h-5 w-5" />
        <span className="font-medium">Ask Bob</span>
      </Button>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss Ask Bob for this page"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute -top-2 -right-2 h-5 w-5 rounded-full flex items-center justify-center shadow transition-colors"
          style={{ backgroundColor: colors.buttonTertiary }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiary;
          }}
        >
          <X className="h-3 w-3 text-black" />
        </button>
      )}
    </div>
  )
}

